import React, { useState } from 'react';
import { Send, Copy, ExternalLink, CheckCircle2, RefreshCw, Loader2, Link as LinkIcon } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';

// ────────────────────────────────────────────────────────────────────────
// PublishBriefModal
// ────────────────────────────────────────────────────────────────────────
// Handles three phases of the "Send to customer" workflow:
//
//   1. `confirm`  — Confirmation step before the API call.
//                    First publish shows a "share your work" tone,
//                    republishes call out that the customer's link
//                    stays stable.
//   2. `working`  — brief loading state while the POST is in-flight.
//   3. `success`  — displays the stable customer link with copy /
//                    open-in-new-tab affordances.
//
// The parent owns the actual API call; the modal exposes an
// `onConfirm()` async hook that returns the publish result. The parent
// also passes any pre-existing publish state so a click-through when
// "nothing has changed" can skip the confirmation step and open the
// success view directly with the existing link.
// ────────────────────────────────────────────────────────────────────────

const GREEN = '#0F5132';
const PURPLE = '#5B47E8';

export default function PublishBriefModal({
  open,
  onOpenChange,
  publishStatus,      // { published, has_unpublished_changes, share_url, share_path, revision, ... }
  onConfirm,          // async () => Promise<publishResult>
}) {
  const [phase, setPhase] = useState('confirm');   // confirm | working | success
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const isPublished = !!publishStatus?.published;
  const hasChanges = !!publishStatus?.has_unpublished_changes;

  // ── URL resolution ─────────────────────────────────────────────────────
  const absoluteFrom = (path, absolute) => {
    if (absolute && /^https?:\/\//i.test(absolute)) return absolute;
    if (!path) return absolute || '';
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    return `${base}${path}`;
  };
  const currentShareUrl = absoluteFrom(publishStatus?.share_path, publishStatus?.share_url);
  const resultShareUrl = absoluteFrom(result?.share_path, result?.share_url);
  const shareUrlToShow = result ? resultShareUrl : currentShareUrl;

  // Republish-with-no-changes: skip confirm, show success with existing link.
  const shortCircuit = isPublished && !hasChanges && !result;

  // Reset internal state when the dialog opens/closes.
  React.useEffect(() => {
    if (open) {
      setPhase(shortCircuit ? 'success' : 'confirm');
      setResult(null);
      setError(null);
    }
  }, [open, shortCircuit]);

  const handleConfirm = async () => {
    setPhase('working');
    setError(null);
    try {
      const r = await onConfirm();
      setResult(r);
      setPhase('success');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'We couldn\'t publish the brief. Try again.';
      setError(detail);
      setPhase('confirm');
    }
  };

  const handleCopy = async () => {
    if (!shareUrlToShow) return;
    try {
      await navigator.clipboard.writeText(shareUrlToShow);
      toast.success('Link copied to clipboard');
    } catch (_) {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = shareUrlToShow;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success('Link copied to clipboard'); }
      catch (__) { toast.error('Copy failed — please copy manually.'); }
      finally { document.body.removeChild(ta); }
    }
  };

  const handleOpen = () => {
    if (!shareUrlToShow) return;
    window.open(shareUrlToShow, '_blank', 'noopener,noreferrer');
  };

  // ── titles / copy per phase ────────────────────────────────────────────
  const confirmTitle = isPublished ? 'Republish updates?' : 'Send to customer?';
  const confirmBody = isPublished ? (
    <>
      <p>Your customer&apos;s <strong>link stays the same</strong>. Republishing creates a new version and updates what they see when they open it.</p>
      <p className="mt-2 text-gray-500">No email or notification is sent — you can share the link however you like.</p>
    </>
  ) : (
    <>
      <p>This creates a public link your customer can view anytime. The link is permanent while the brief is active, and it always shows the latest published version.</p>
      <p className="mt-2 text-gray-500">No email or notification is sent — you&apos;ll get the link to share on your own.</p>
    </>
  );

  const successTitle = result ? (result.is_republish ? 'Republished' : 'Published — link ready') : 'Link ready to share';
  const successBody = result?.is_republish
    ? "The customer's link stays the same. Anyone opening it will now see your latest changes."
    : (result
        ? "Your customer can now open this link anytime to view the brief you've built."
        : "The brief is already published. Share the link below anytime.");

  const confirmCta = isPublished
    ? (<><RefreshCw className="w-4 h-4" aria-hidden />Republish updates</>)
    : (<><Send className="w-4 h-4" aria-hidden />Publish &amp; get link</>);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="publish-brief-modal"
      >
        {phase === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle data-testid="publish-modal-title">{confirmTitle}</DialogTitle>
              <DialogDescription className="text-[13.5px] text-gray-700 pt-2 leading-relaxed">
                {confirmBody}
              </DialogDescription>
            </DialogHeader>
            {error ? (
              <div
                className="mt-2 text-[12.5px] rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2"
                data-testid="publish-modal-error"
              >
                {error}
              </div>
            ) : null}
            <DialogFooter className="mt-4 gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="publish-modal-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                className="gap-1.5 text-white hover:opacity-90"
                style={{ backgroundColor: PURPLE }}
                data-testid="confirm-publish-btn"
              >
                {confirmCta}
              </Button>
            </DialogFooter>
          </>
        )}

        {phase === 'working' && (
          <div className="py-8 flex flex-col items-center gap-3 text-center" data-testid="publish-modal-working">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: PURPLE }} />
            <p className="text-[13.5px] text-gray-700">Sealing the brief and preparing the customer link…</p>
          </div>
        )}

        {phase === 'success' && (
          <div data-testid="publish-success-modal">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" style={{ color: GREEN }} aria-hidden />
                <DialogTitle data-testid="publish-success-title">{successTitle}</DialogTitle>
              </div>
              <DialogDescription className="text-[13.5px] text-gray-700 pt-2 leading-relaxed">
                {successBody}
              </DialogDescription>
            </DialogHeader>

            <label
              className="mt-4 block text-[11px] font-semibold uppercase tracking-[0.1em] text-gray-500"
              htmlFor="publish-share-url"
            >
              Customer link
            </label>
            <div className="mt-1.5 flex items-stretch gap-2">
              <div className="flex-1 flex items-center gap-2 rounded-md border border-[#E8DEC7] bg-[#FFF7EE] px-3 py-2 text-[12.5px] text-gray-800 min-w-0">
                <LinkIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" aria-hidden />
                <input
                  id="publish-share-url"
                  readOnly
                  value={shareUrlToShow || ''}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 bg-transparent outline-none truncate font-mono text-[12px]"
                  data-testid="publish-share-url-input"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className="gap-1.5 shrink-0"
                data-testid="copy-share-link-btn"
              >
                <Copy className="w-4 h-4" aria-hidden />
                Copy
              </Button>
            </div>

            <div className="mt-2 flex items-center justify-between">
              <div className="text-[11.5px] text-gray-500">
                {result?.revision ? (
                  <>Published version <strong className="text-gray-700">v{result.revision}</strong></>
                ) : (publishStatus?.revision ? (
                  <>Published version <strong className="text-gray-700">v{publishStatus.revision}</strong></>
                ) : null)}
              </div>
              <button
                type="button"
                onClick={handleOpen}
                className="inline-flex items-center gap-1 text-[12.5px] font-medium hover:opacity-80"
                style={{ color: PURPLE }}
                data-testid="open-share-link-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                Open in new tab
              </button>
            </div>

            <DialogFooter className="mt-5">
              <Button
                onClick={() => onOpenChange(false)}
                className="text-white hover:opacity-90"
                style={{ backgroundColor: GREEN }}
                data-testid="publish-success-close"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
